import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import { TopBar } from 'components/organisms';
import ExchangeRates from 'components/organisms/forms/statements/tabs/ExchangeRates';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import { useParamId } from 'hooks/useParamId';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useGetStatementRowsInfinite,
  useGetStatementSummaryRowsInfinite,
  useGetStatementUnrelatedRowsInfinite,
} from 'services/statements';
import {
  CURRENCY_ID,
  IStatement,
  STATEMENT_CONSISTANCY,
  STATEMENT_STATUS,
} from 'services/statements/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { IFormProps } from '../types';
import Errors from './tabs/Errors';
import Rows from './tabs/Rows';
import SummaryRows from './tabs/SummaryRows';
import UnrelatedRows from './tabs/UnrelatedRows';

export enum statementsTabs {
  exchangeRates,
  rows,
  unrelatedRows,
  summaryRows,
  errors,
}

export const initialStatement: IStatement = {
  id: '',
  salesChannelId: '',
  settlementPeriodId: '',
  providerId: '',
  providerName: '',
  statementStatus: STATEMENT_STATUS.unknown,
  defaultCurrency: CURRENCY_ID.PLN,
  consistency: STATEMENT_CONSISTANCY.consistent,
  errors: [],
  defaultTotalValue: 0,
  statementFileId: '',
  statementFilePath: '',
  settlementPeriodName: { name: '', year: -1 },
  salesChannelName: '',
  exchangeRates: [],
};

interface ExchangeRatesFormProps
  extends FormikConfig<IStatement>,
    IFormProps,
    II18nextPrefix {}

interface ExchangeRatesFormComponent extends React.FC<ExchangeRatesFormProps> {
  getRouterTabs?: (
    disabledChildren?: string[]
  ) => { path: string; element: JSX.Element }[];
}

const ExchangeRatesForm: ExchangeRatesFormComponent = ({
  i18nextPrefix = '',
  initialValues = initialStatement,
  tittle = '',
  onSubmit = () => {},
  topbarContent,
  status,
}) => {
  const navigate = useNavigate();

  const id = useParamId('statements');

  const { t } = useTranslation();

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      initialStatus={status}
      onSubmit={onSubmit}
      validateOnChange={true}
      validateOnBlur={true}>
      {({ values }) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              title={getSettlementPeriodName(values.settlementPeriodName) ?? '-'}
              topbarContent={topbarContent}>
              <InfoBox>
                <InfoBox.Items columns={1}>
                  <FormInput.Text
                    name='salesChannelName'
                    label={`${t('input.label.salesChannel')}`}
                  />
                  <FormInput.Text
                    name='providerName'
                    label={`${t('input.label.provider')}`}
                  />

                  <FormInput.Number
                    name='defaultTotalValue'
                    label={`${t('input.label.totalValue')}`}
                    suffix={` ${CURRENCY_ID[values.defaultCurrency]}`}
                  />
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: statementsTabs.exchangeRates,
                    name: 'exchangeRate',
                  },

                  {
                    value: statementsTabs.rows,
                    name: 'rows',
                  },

                  {
                    value: statementsTabs.summaryRows,
                    name: 'summaryRows',
                  },

                  ...(values.hasUnrelatedRows
                    ? [
                        {
                          value: statementsTabs.unrelatedRows,
                          name: 'unrelatedRows',
                        },
                      ]
                    : []),
                  ...(values.errors.length
                    ? [
                        {
                          value: statementsTabs.errors,
                          name: 'errors',
                        },
                      ]
                    : []),
                ]}
                disabled={
                  values.statementStatus !== STATEMENT_STATUS.done
                    ? [statementsTabs.summaryRows, statementsTabs.rows]
                    : undefined
                }
                i18nextPrefix={'statements'}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ExchangeRatesForm.getRouterTabs = (disabledChildren: string[] = []) =>
  [
    { path: 'exchangeRate', element: <ExchangeRates /> },
    { path: 'unrelatedRows', element: <UnrelatedRows /> },
    { path: 'summaryRows', element: <SummaryRows /> },
    { path: 'rows', element: <Rows /> },
    { path: 'errors', element: <Errors /> },
  ].filter(child => !disabledChildren.includes(child.path));

export default ExchangeRatesForm;
